/**
 * THE MONTH GRID AND THE QUICK PICKS (V0.51.1) — pure, DOM-free, clock-free.
 *
 * `components/DatePicker.tsx` draws what this computes, and it is the whole of the calendar's
 * arithmetic. Split out for the reason `lib/todos.ts` states about itself: today is always a
 * PARAMETER here, so a test can ask about any day of any year, and nothing in this directory
 * reads the clock.
 *
 * WHY THERE IS A CALENDAR AT ALL. The app must run air-gapped — `connect-src 'self'`, bundle
 * rather than CDN — so a date-picker library would have to be vendored whole and kept. The
 * grid is thirty lines of calendar arithmetic; the dependency is not worth its own supply
 * chain, and the five date fields in this app then share ONE implementation rather than the
 * browser's, which looks different in every browser and cannot carry a quick pick.
 *
 * DAYS ARE STRINGS, `YYYY-MM-DD`, and months are `YYYY-MM` — compared and sorted as strings,
 * never through `Date`, for the reason `lib/todos.ts` gives at length: the moment a day becomes
 * a `Date` somebody renders it in local time and it lands on the day before for everybody west
 * of Greenwich. `Date` appears here only as UTC calendar arithmetic, which is exactly what
 * `addDays` already does.
 */
import { addDays } from './todos';

/** `YYYY-MM`. The month a grid is drawn for. */
export type MonthKey = string;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The month a day falls in. */
export const monthOf = (day: string): MonthKey => day.slice(0, 7);

/** The first day of a month, as a day key. */
export const firstDayOf = (month: MonthKey): string => `${month}-01`;

/** `month` shifted by `n` months, negative allowed. Through UTC, so no DST anywhere near it. */
export function shiftMonth(month: MonthKey, n: number): MonthKey {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
}

/** One cell of the grid: which day it is, and whether it belongs to a neighbouring month. */
export interface MonthCell {
  day: string;
  /** True for the leading and trailing days that fill the first and last weeks. */
  outside: boolean;
  /** 0 = Monday … 6 = Sunday. */
  weekday: number;
}

/**
 * Six weeks of cells covering `month`, MONDAY FIRST.
 *
 * ALWAYS 42 cells, never a ragged 35 or 42 depending on the month: a grid that changes height
 * as you page through it makes the buttons move under the cursor, and the next click lands on
 * the wrong day. The fixed six weeks is what keeps every day in the same place.
 *
 * Monday first because the app's audience writes its weeks that way; the weekday labels are a
 * translation and live in `i18n/dates.ts`, not here.
 */
export function monthGrid(month: MonthKey): MonthCell[] {
  const [y, m] = month.split('-').map(Number);
  // `getUTCDay` is Sunday-first; this rotates it so Monday is 0.
  const lead = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
  const start = addDays(firstDayOf(month), -lead);
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const day = addDays(start, i);
    cells.push({
      day,
      outside: day.slice(0, 7) !== month,
      weekday: i % 7,
    });
  }
  return cells;
}

/** The next Monday STRICTLY after `today` — never today itself, even when today is a Monday. */
export function nextMonday(today: string): string {
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(today, (8 - dow) % 7 || 7);
}

/** One quick pick: the i18n key for its label, and the day it sets. */
export interface QuickPick {
  key: 'today' | 'tomorrow' | 'nextMonday' | 'inAWeek' | 'inAMonth';
  day: string;
}

/**
 * The shortcuts above the grid.
 *
 * These five because they are how people actually say a due date out loud — "tomorrow", "next
 * Monday", "in a week". Paging a grid to reach tomorrow is three interactions for the single
 * most common answer, which is the thing the native input got wrong and the reason this
 * component exists at all.
 */
export const quickPicks = (today: string): QuickPick[] => [
  { key: 'today', day: today },
  { key: 'tomorrow', day: addDays(today, 1) },
  { key: 'nextMonday', day: nextMonday(today) },
  { key: 'inAWeek', day: addDays(today, 7) },
  { key: 'inAMonth', day: addDays(today, 30) },
];

/** Is `day` inside the optional bounds? Both ends inclusive; an absent bound is no bound. */
export const dayInRange = (day: string, min?: string | null, max?: string | null): boolean =>
  (!min || day >= min) && (!max || day <= max);
